import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useLocale, useTranslations } from 'gt-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { SecretDialog } from '@/components/oauth/secret-dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { errorMessage } from '@/lib/error-message'
import { formatDate } from '@/lib/intl-format'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/oidc-clients/$id')({
  component: OidcClientDetailPage,
})

interface ClientShape {
  clientId: string
  displayName: string
  ownerEmail: string
  redirectUris: string[]
  scopesAllowed: string[]
  active: boolean
  createdAt: string | Date
}

function OidcClientDetailPage() {
  const t = useTranslations()
  const locale = useLocale()
  const navigate = useNavigate()
  const { id } = Route.useParams()

  const detailQ = useQuery(orpc.external.oidcClients.get.queryOptions({ input: { clientId: id } }))

  const [rotated, setRotated] = useState<string | null>(null)
  const [rotateConfirm, setRotateConfirm] = useState(false)
  const [deactivateConfirm, setDeactivateConfirm] = useState(false)

  const rotate = useMutation(
    orpc.external.oidcClients.rotate.mutationOptions({
      onSuccess: (res) => {
        const typed = res as { clientSecret: string }
        setRotated(typed.clientSecret)
        setRotateConfirm(false)
      },
      onError: (e: Error) => toast.error(errorMessage(e, t)),
    }),
  )

  const deactivate = useMutation(
    orpc.external.oidcClients.deactivate.mutationOptions({
      onSuccess: () => {
        toast.success(t('oidcClients.deactivated'))
        setDeactivateConfirm(false)
        detailQ.refetch()
      },
      onError: (e: Error) => toast.error(errorMessage(e, t)),
    }),
  )

  if (detailQ.isPending) {
    return (
      <div className="container mx-auto py-6 px-4 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (detailQ.error || !detailQ.data) {
    return (
      <div className="container mx-auto py-6 px-4">
        <p className="text-sm text-muted-foreground">{t('oidcClients.loadError')}</p>
        <Button variant="ghost" onClick={() => navigate({ to: '/settings/oidc-clients' })}>
          {t('oidcClients.back')}
        </Button>
      </div>
    )
  }

  const data = detailQ.data as ClientShape
  const created = data.createdAt instanceof Date ? data.createdAt : new Date(data.createdAt)

  return (
    <div className="container mx-auto py-6 px-4 space-y-6 max-w-3xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{data.displayName}</h1>
          <p className="text-sm text-muted-foreground font-mono">{data.clientId}</p>
        </div>
        {data.active ? (
          <Badge variant="outline" className="bg-green-100 text-green-800">
            {t('oidcClients.active')}
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-amber-100 text-amber-800">
            {t('oidcClients.inactive')}
          </Badge>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('oidcClients.details')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Field label={t('oidcClients.ownerEmail')} value={data.ownerEmail} />
          <Field label={t('oidcClients.createdAt')} value={formatDate(created, locale)} />
          <div>
            <div className="text-muted-foreground mb-1">{t('oidcClients.redirectUris')}</div>
            <ul className="space-y-1">
              {data.redirectUris.map((uri) => (
                <li key={uri} className="font-mono text-xs">
                  {uri}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">{t('oidcClients.scopesAllowed')}</div>
            <div className="flex flex-wrap gap-1">
              {data.scopesAllowed.map((s) => (
                <Badge key={s} variant="secondary" className="font-mono text-xs">
                  {s}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('oidcClients.actions')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setRotateConfirm(true)} disabled={!data.active}>
              {t('oidcClients.rotate')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => setDeactivateConfirm(true)}
              disabled={!data.active}
            >
              {t('oidcClients.deactivate')}
            </Button>
            <Button variant="ghost" onClick={() => navigate({ to: '/settings/oidc-clients' })}>
              {t('oidcClients.back')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={rotateConfirm} onOpenChange={setRotateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('oidcClients.rotate')}</AlertDialogTitle>
            <AlertDialogDescription>{t('oidcClients.rotateConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => rotate.mutate({ clientId: id })}
              disabled={rotate.isPending}
            >
              {t('oidcClients.rotate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deactivateConfirm} onOpenChange={setDeactivateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('oidcClients.deactivate')}</AlertDialogTitle>
            <AlertDialogDescription>{t('oidcClients.deactivateConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deactivate.mutate({ clientId: id })}
              disabled={deactivate.isPending}
            >
              {t('oidcClients.deactivate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SecretDialog
        open={rotated !== null}
        secret={rotated}
        clientId={data.clientId}
        onClose={() => setRotated(null)}
      />
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <div className="text-muted-foreground">{label}</div>
      <div className="col-span-2">{value}</div>
    </div>
  )
}
